import { useCallback, useMemo } from "react";
import _ from "lodash";

import Button from "components/Button";

import { useDealSidepanel } from "entities/Deal/components/Sidepanel/useDealSidepanel";
import {
  TABS,
  TASKS_TAB_SUBTABS,
} from "entities/Deal/components/Sidepanel/utils";
import { IPackageTab } from "entities/Package/sdk";
import { ITaskChannel } from "entities/Task/sdk";

interface IDueDiligenceTabOpenTaskButton {
  tab: IPackageTab;
  taskChannels: Array<ITaskChannel>;
  dealId: number;
  packageId: number;
  updatePackage: ({ message }: { message?: string }) => void;
  taskButtonDataTestid?: string;
  taskMessagesButtonDataTestid?: string;
}

const DueDiligenceTabOpenTaskButton = ({
  tab,
  taskChannels,
  dealId,
  packageId,
  updatePackage,
  taskButtonDataTestid,
  taskMessagesButtonDataTestid,
}: IDueDiligenceTabOpenTaskButton) => {
  const { show } = useDealSidepanel();

  const unreadLabel = useMemo(
    () => _.sumBy(taskChannels, "unread_count"),
    [taskChannels]
  );

  const handleOpenTaskChat = useCallback(() => {
    const taskInitialData = {
      title: tab.name,
      company_package: packageId,
      company_tab: tab.tab_id,
    };

    const taskChannelsWithUnreadMessages = _.filter(
      taskChannels,
      (taskChannel) => taskChannel.unread_count > 0
    );

    if (taskChannelsWithUnreadMessages.length === 1) {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: () => updatePackage({}),
          subTab: TASKS_TAB_SUBTABS.CHAT,
          taskChannel: taskChannelsWithUnreadMessages[0],
          dealId,
          taskTab: tab,
          taskInitialData,
        },
      });
    } else {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: () => updatePackage({}),
          dealId,
          taskTab: tab,
          taskInitialData,
        },
      });
    }
  }, [show, dealId, taskChannels, updatePackage, tab, packageId]);

  const handleOpenTask = useCallback(() => {
    const taskInitialData = {
      title: tab.name,
      company_package: packageId,
      company_tab: tab.tab_id,
    };
    if (taskChannels.length === 1) {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: () => updatePackage({}),
          dealId,
          taskTab: tab,
          subTab: TASKS_TAB_SUBTABS.DETAILS,
          taskChannel: taskChannels[0],
          taskInitialData,
        },
      });
    } else {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: () => updatePackage({}),
          dealId,
          taskTab: tab,
          taskInitialData,
        },
      });
    }
  }, [show, dealId, updatePackage, taskChannels, tab, packageId]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpenTask}
        data-testid={taskButtonDataTestid}
      >
        {taskChannels.length} open task{taskChannels.length > 1 ? "s" : ""}
      </Button>
      {unreadLabel > 0 && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenTaskChat}
          data-testid={taskMessagesButtonDataTestid}
        >
          {unreadLabel} message{unreadLabel > 1 ? "s" : ""}
        </Button>
      )}
    </>
  );
};

export default DueDiligenceTabOpenTaskButton;
